import { React, useState, useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbar from "examples/Navigation/Navbar";
import DefaultFooter from "examples/Footers/Footer/footer";
import routes from "routes";
import footerRoutes from "footer.routes";
import gsap from "gsap";

// Header & Case Study Images
import CaseStudiesHeader from "assets/images/CaseStudiesHeader.jpg";
import SeletarMall from "assets/images/SeletarMall.jpg";
import MIMOSBhd from "assets/images/MIMOSBhd..jpg";
import TNBJalanTimur from "assets/images/TNBJalanTimur.jpg";
import MelakaInternational from "assets/images/melakainternational.jpeg";
import KPJAmpangPuteri from "assets/images/kpgampangputri.jpg";
import BTMC from "assets/images/btmc.jpeg";

// Industrial Case Studies Images
import ComputimeFactory from "assets/images/Computime.jpeg.jpg";
import KnowlesManufacturing from "assets/images/Knowles.jpg";

// Logo Images (now referenced directly)
import KPJAmpangLogo from "assets/images/KPJAmpangLogo.png";
import BTMCLogo from "assets/images/BTMCLogo.png";
import MITCLogo from "assets/images/MITCLogo.jpg";
import MIMOSLogo from "assets/images/MIMOSLogo.png";
import KnowlesLogo from "assets/images/Knowles logo.avif";
import TNBLogo from "assets/images/TNBLogo.webp";
import ComputimeLogo from "assets/images/ComputimeLogo.png";
import SeletarMallLogo from "assets/images/SeletarMallLogo.jpg";

const clientLogos = [
  { name: "KPJ Ampang", logo: KPJAmpangLogo },
  { name: "BTMC", logo: BTMCLogo },
  { name: "MITC", logo: MITCLogo },
  { name: "MIMOS", logo: MIMOSLogo },
  { name: "Knowles", logo: KnowlesLogo },
  { name: "TNB", logo: TNBLogo },
  { name: "Computime", logo: ComputimeLogo },
  { name: "Seletar Mall", logo: SeletarMallLogo },
];

const caseStudies = {
  Commercial: [
    {
      name: "Seletar Mall",
      location: "Singapore",
      type: "Commercial",
      savings: "500,105 kJ",
      emissions: "48.1342 Tonne CO2e",
      avgSavings: "14.61%",
      image: SeletarMall,
    },
    {
      name: "Melaka International Trade Centre",
      location: "Malaysia",
      type: "Commercial",
      savings: "2165 MWh/RM 252,427",
      emissions: "135.24 Tonne CO2e",
      avgSavings: "16.17%",
      image: MelakaInternational,
    },
    {
      name: "TNB Jalan Timur",
      location: "Malaysia",
      type: "Commercial",
      savings: "761.5 MWh/RM 100,306",
      emissions: "52.6 Tonne CO2e",
      avgSavings: "15%",
      image: TNBJalanTimur,
    },
    {
      name: "KPJ Ampang Puteri",
      location: "Malaysia",
      type: "Commercial",
      savings: "70.49 MWh/RM 132,067",
      emissions: "72.6 Tonne CO2e",
      avgSavings: "12%",
      image: KPJAmpangPuteri,
    },
    {
      name: "BTMC",
      location: "Malaysia",
      type: "Commercial",
      savings: "22.8660 MWh/RM 11601.43",
      emissions: "17.5639 Tonne CO2e",
      avgSavings: "12.2%",
      image: BTMC,
    },
  ],
  Industrial: [
    {
      name: "MIMOS Wafer Fab Manufacturing",
      location: "Malaysia",
      type: "Industrial",
      savings: "1205 MWh/RM 494,050",
      emissions: "836.3 Tonne CO2e",
      avgSavings: "16%",
      image: MIMOSBhd,
    },
    {
      name: "Computime Electronic Factory",
      location: "Malaysia",
      type: "Industrial",
      savings: "54.32 MWh/RM 107,215",
      emissions: "37.69 Tonne CO2e",
      avgSavings: "10%",
      image: ComputimeFactory,
    },
    {
      name: "Knowles",
      location: "Malaysia",
      type: "Manufacturing",
      savings: "761.15 MWh/RM 100,306",
      emissions: "52.85 Tonne CO2e",
      avgSavings: "15%",
      image: KnowlesManufacturing,
    },
  ],
};

function CaseStudies() {
  const [selectedCategory, setSelectedCategory] = useState("Commercial");
  const carouselRef = useRef(null);

  // Carousel (Our Clients) auto-scroll & manual scroll effect
  useEffect(() => {
    const menu = carouselRef.current;
    if (!menu) return;

    let items = menu.querySelectorAll('.menu--item');
    let itemWidth = items[0] ? items[0].clientWidth : 0;
    let wrapWidth = items.length * itemWidth;

    let scrollSpeed = 0;
    let oldScrollX = 0;
    let scrollX = 0;
    let x = 0;
    // Increased interval to 3000ms for a slower auto-scroll.
    const autoScrollInterval = 3000;
    let autoScrollTimer = null;

    // Linear interpolation helper.
    const lerp = (v0, v1, t) => v0 * (1 - t) + v1 * t;

    // Update positions using GSAP; we calculate a center offset so that the logos remain centered.
    const dispose = (scroll) => {
      const centerOffset = (menu.clientWidth - wrapWidth) / 2;
      gsap.set(items, {
        x: (i) => i * itemWidth + scroll + centerOffset,
        modifiers: {
          x: (xVal) => {
            // Adjust the wrap boundaries by including the centerOffset.
            const s = gsap.utils.wrap(-itemWidth + centerOffset, wrapWidth - itemWidth + centerOffset, parseFloat(xVal));
            return `${s}px`;
          }
        }
      });
    };
    dispose(0);

    // Auto-scroll: move to the next item periodically.
    const startAutoScroll = () => {
      autoScrollTimer = setInterval(() => {
        scrollX -= itemWidth;
      }, autoScrollInterval);
    };
    const stopAutoScroll = () => clearInterval(autoScrollTimer);

    // Mouse wheel scrolling; prevent default so the page doesn't scroll.
    const handleMouseWheel = (e) => {
      e.preventDefault();
      scrollX -= e.deltaY * 0.9;
      stopAutoScroll();
    };

    // Touch & drag events for mobile/desktop; prevent default to avoid page scrolling.
    let touchStart = 0;
    let isDragging = false;
    const handleTouchStart = (e) => {
      touchStart = e.touches ? e.touches[0].clientX : e.clientX;
      isDragging = true;
      menu.classList.add('is-dragging');
      stopAutoScroll();
    };
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      scrollX += (currentX - touchStart) * 2.5;
      touchStart = currentX;
    };
    const handleTouchEnd = () => {
      isDragging = false;
      menu.classList.remove('is-dragging');
      startAutoScroll();
    };

    // Prevent text selection during drag.
    const preventSelect = (e) => { e.preventDefault(); return false; };

    // Add event listeners to the carousel container.
    menu.addEventListener('wheel', handleMouseWheel, { passive: false });
    menu.addEventListener('touchstart', handleTouchStart);
    menu.addEventListener('touchmove', handleTouchMove, { passive: false });
    menu.addEventListener('touchend', handleTouchEnd);
    menu.addEventListener('mousedown', handleTouchStart);
    menu.addEventListener('mousemove', handleTouchMove);
    menu.addEventListener('mouseleave', handleTouchEnd);
    menu.addEventListener('mouseup', handleTouchEnd);
    menu.addEventListener('selectstart', preventSelect);

    // Update dimensions on window resize.
    const handleResize = () => {
      itemWidth = items[0] ? items[0].clientWidth : 0;
      wrapWidth = items.length * itemWidth;
    };
    window.addEventListener('resize', handleResize);

    let rafId;
    const render = () => {
      rafId = requestAnimationFrame(render);
      x = lerp(x, scrollX, 0.1);
      dispose(x);
      scrollSpeed = x - oldScrollX;
      oldScrollX = x;

      // Subtle visual effects based on scroll speed.
      gsap.to(items, {
        skewX: -scrollSpeed * 0.2,
        rotate: scrollSpeed * 0.01,
        scale: 1 - Math.min(100, Math.abs(scrollSpeed)) * 0.003,
        duration: 0.1,
        ease: "power1.out"
      });
    };
    render();
    startAutoScroll();

    // Cleanup on unmount.
    return () => {
      clearInterval(autoScrollTimer);
      cancelAnimationFrame(rafId);
      menu.removeEventListener('wheel', handleMouseWheel);
      menu.removeEventListener('touchstart', handleTouchStart);
      menu.removeEventListener('touchmove', handleTouchMove);
      menu.removeEventListener('touchend', handleTouchEnd);
      menu.removeEventListener('mousedown', handleTouchStart);
      menu.removeEventListener('mousemove', handleTouchMove);
      menu.removeEventListener('mouseleave', handleTouchEnd);
      menu.removeEventListener('mouseup', handleTouchEnd);
      menu.removeEventListener('selectstart', preventSelect);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <DefaultNavbar routes={routes} transparent light />

      {/* Header Image with Title */}
      <MKBox
        position="relative"
        width="100%"
        height="55vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <img
          src={CaseStudiesHeader}
          alt="Top Image"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center"
          }}
        />
        <MKTypography
          variant="h1"
          component="div"
          color="white"
          sx={{
            position: "absolute",
            fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
            fontWeight: "bold",
            mt: 8,
            textAlign: "center",
            textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
          }}
        >
          Client&apos;s Success
        </MKTypography>
      </MKBox>

      <Card sx={{ background: "white", borderRadius: "0" }}>
        {/* Notable Clients Section */}
        <MKBox
          sx={{
            p: 4,
            mx: { xs: 2, lg: 6 },
            my: 4,
            backgroundColor: "white",
          }}
        >
          <MKTypography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            textAlign="center"
            fontSize="24px"
            sx={{ mb: 4 }}
          >
            Notable Clients
          </MKTypography>

          <Grid container spacing={3} justifyContent="center">
  {/* Client 1 */}
  <Grid item xs={12} sm={6} md={4}>
    <Card elevation={0} sx={{ height: "480px", display: "flex", flexDirection: "column" }}>
      <MKBox
        component="img"
        src={SeletarMall}
        alt="Seletar Mall"
        sx={{
          width: "100%",
          height: "250px",
          objectFit: "cover",
          borderRadius: "0px"
        }}
      />
      <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
        <MKTypography variant="h6" fontWeight="bold" textAlign="center" fontSize="24px">
          Seletar Mall
        </MKTypography>
        <MKTypography variant="body1" fontWeight="light" textAlign="center" fontSize="18px">
          We partnered with Seletar Mall in Singapore to implement a chiller optimization solution. Through smart cooling strategies and real‑time load adjustments, the mall achieved over 10% annual energy savings and cost reductions.
        </MKTypography>
      </CardContent>
    </Card>
  </Grid>

  {/* Client 2 */}
  <Grid item xs={12} sm={6} md={4}>
    <Card elevation={0} sx={{ height: "480px", display: "flex", flexDirection: "column" }}>
      <MKBox
        component="img"
        src={MIMOSBhd}
        alt="MIMOS Bhd."
        sx={{
          width: "100%",
          height: "250px",
          objectFit: "cover",
          borderRadius: "0px"
        }}
      />
      <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
        <MKTypography variant="h6" fontWeight="bold" textAlign="center" fontSize="24px">
          MIMOS Bhd.
        </MKTypography>
        <MKTypography variant="body1" fontWeight="light" textAlign="center" fontSize="18px">
          We collaborated with MIMOS Berhad, Malaysia’s leading R&amp;D center, to optimize its wafer fabrication facility’s cooling system. The success of this initiative has led to further expansions.
        </MKTypography>
      </CardContent>
    </Card>
  </Grid>

  {/* Client 3 */}
  <Grid item xs={12} sm={6} md={4}>
    <Card elevation={0} sx={{ height: "480px", display: "flex", flexDirection: "column" }}>
      <MKBox
        component="img"
        src={TNBJalanTimur}
        alt="TNB Jalan Timur"
        sx={{
          width: "100%",
          height: "250px",
          objectFit: "cover",
          borderRadius: "0px"
        }}
      />
      <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
        <MKTypography variant="h6" fontWeight="bold" textAlign="center" fontSize="24px">
          TNB Jalan Timur
        </MKTypography>
        <MKTypography variant="body1" fontWeight="light" textAlign="center" fontSize="18px">
          In collaboration with TNB Jalan Timur, we implemented a chiller optimization system to enhance energy efficiency. The system stabilized after six months, ensuring long‑term sustainability and cost savings.
        </MKTypography>
      </CardContent>
    </Card>
  </Grid>
</Grid>


        </MKBox>

        {/* Other Case Studies Section */}
        <MKBox
          sx={{
            p: 4,
            mx: { xs: 2, lg: 6 },
            my: 4,
            mt: 2,
            backgroundColor: "white",
          }}
        >
          <MKTypography variant="h4" fontWeight="bold" gutterBottom textAlign="center" fontSize="24px" sx={{ mb: 4 }}>
            Other Case Studies
          </MKTypography>

          {/* Toggle Buttons */}
          <MKBox textAlign="center" display="flex" justifyContent="center" sx={{ mb: 3 }}>
            <Button
              variant={selectedCategory === "Commercial" ? "contained" : "outlined"}
              onClick={() => setSelectedCategory("Commercial")}
              sx={{
                borderRadius: "12px",
                mx: 1,
                backgroundColor: selectedCategory === "Commercial" ? "#8CBCDB" : "white",
                color: selectedCategory === "Commercial" ? "white" : "#344767",
                borderColor: "#8CBCDB",
                width: "250px",
                "&:hover": {
                  backgroundColor: "#6AA3C1",
                  color: "white",
                },
              }}
            >
              Commercial Buildings
            </Button>
            <Button
              variant={selectedCategory === "Industrial" ? "contained" : "outlined"}
              onClick={() => setSelectedCategory("Industrial")}
              sx={{
                borderRadius: "12px",
                mx: 1,
                backgroundColor: selectedCategory === "Industrial" ? "#8CBCDB" : "white",
                color: selectedCategory === "Industrial" ? "white" : "#344767",
                borderColor: "#8CBCDB",
                width: "250px",
                "&:hover": {
                  backgroundColor: "#6AA3C1",
                  color: "white",
                  cursor: "pointer"
                },
              }}
            >
              Industrial Buildings
            </Button>
          </MKBox>

          {/* Display Case Studies Based on Selection */}
          <Grid container spacing={3} justifyContent="center">
            {caseStudies[selectedCategory].map((study, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card elevation={3}>
                  <MKBox
                    component="img"
                    src={study.image}
                    alt={study.name}
                    sx={{
                      width: "100%",
                      height: "200px",
                      objectFit: "cover",
                    }}
                  />
                  <CardContent sx={{ p: 0, m: 0, background: "#E3ECF1" }}>
                    {[
                      { label: "Name", value: study.name },
                      { label: "Location", value: study.location },
                      { label: "Segment Type", value: study.type },
                      { label: "Total Savings", value: study.savings },
                      { label: "Total Emission Saved", value: study.emissions },
                      { label: "Average Savings", value: study.avgSavings },
                    ].map((item, rowIndex) => (
                      <MKBox
                        key={rowIndex}
                        sx={{
                          backgroundColor: rowIndex % 2 === 0 ? "#E3ECF1" : "#8CBCDB",
                          width: "100%",
                          display: "block",
                          p: 1,
                          m: 0,
                        }}
                      >
                        <MKTypography variant="body2" textAlign="center" sx={{ width: "100%" }}>
                          {item.label}: {item.value}
                        </MKTypography>
                      </MKBox>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MKBox>

{/* Our Clients Carousel */}
<MKBox textAlign="center" sx={{ p: 4, backgroundColor: "white", mb: 5 }}>
  <MKTypography variant="h4" fontWeight="bold" sx={{ mb: 4 }}>
    Our Clients
  </MKTypography>
  <div
    ref={carouselRef}
    className="menu"
    style={{
      position: "relative",
      overflow: "hidden",
      width: "80%", // Adjusted width to keep it centered on all screen sizes
      maxWidth: "80%", // Ensures it doesn’t stretch too much on larger screens
      height: "120px",
      margin: "0 auto", // Centers it horizontally
      paddingLeft: "10%", // Adjusts left padding to balance alignment
      paddingRight: "10%", // Adds right padding to balance logos
    }}
  >
    {clientLogos.map((client, index) => (
      <div
        key={index}
        className="menu--item"
        style={{
          position: "absolute",
          width: "200px",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MKBox
          component="img"
          src={client.logo}
          alt={client.name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </div>
    ))}
  </div>
</MKBox>

      </Card>

      {/* Footer */}
      <MKBox pt={4} px={0} mt={-3.95}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default CaseStudies;
